import { forwardRef, memo, useCallback, useEffect, useState, type ReactNode, type SyntheticEvent } from 'react';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Snackbar, { type SnackbarCloseReason } from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
// local imports
import { anchorOrigin, SNACKBAR_AUTOHIDE_DURATION, SNACKBAR_ELEVATION } from '../config/params';
import { type Values } from '../helpers/intl';

export type ActionSnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

// white on `error.main` is 3.68 : 1; on `error.dark` it clears WCAG AA (4.5 : 1)
const errorSx: SxProps<Theme> = { bgcolor: 'error.dark' } as const;

type ActionSnackbarProps = {
  /**
   * Latches: the message re-shows only on a false -> true transition, never while `open` stays true.
   * A caller reporting the same failure twice must clear its failure state first, or the second is silent.
   */
  readonly open?: boolean | null;
  readonly severity: ActionSnackbarSeverity;
  readonly message: string;
  readonly values?: Values;
  readonly long?: boolean;
  readonly action?: ReactNode;
  readonly onDismiss?: () => void;
};

const ActionSnackbar = forwardRef<HTMLDivElement, ActionSnackbarProps>(({
  open = false,
  severity,
  message,
  values,
  long = false,
  action,
  onDismiss
}, ref) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (open) setSnackbarOpen(true);
  }, [open]);

  const handleSnackbarClose = useCallback((_event: Event | SyntheticEvent, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
    onDismiss?.();
  }, [onDismiss]);

  const alert = (
    <Alert
        ref={typeof jest === 'undefined' ? undefined : ref}
        action={action}
        onClose={handleSnackbarClose}
        severity={severity}
        variant={severity === 'error' ? 'filled' : 'standard'}
        elevation={SNACKBAR_ELEVATION}
        sx={severity === 'error' ? errorSx : undefined}
    >
      <Typography variant="body1">
        <FormattedMessage id={message} values={values} defaultMessage={message}/>
      </Typography>
    </Alert>
  );

  return (typeof jest === 'undefined' && (
    <Snackbar
        ref={ref}
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        autoHideDuration={long ? 2 * SNACKBAR_AUTOHIDE_DURATION : SNACKBAR_AUTOHIDE_DURATION}
        anchorOrigin={anchorOrigin}
    >
      {alert}
    </Snackbar>
  )) || (snackbarOpen ? alert : null);
});

ActionSnackbar.displayName = 'ActionSnackbar';

export default memo(ActionSnackbar);
